import { ProductModifierGroupDraft } from '@features/catalog/models/product-form-draft.models';
import { catalogLabelExistsIn } from '@features/catalog/utils/catalog-label-normalize';

export type ModifierGroupNameConflictKind = 'catalog-duplicate' | 'sibling-duplicate';

export interface ModifierGroupNameConflict {
  kind: ModifierGroupNameConflictKind;
  groupTempId: string;
  name: string;
}

/** Grupos inline (sin definitionId) con nombre duplicado en catálogo o entre sí. */
export function findModifierGroupNameConflicts(
  groups: readonly ProductModifierGroupDraft[],
  catalogNames: readonly string[]
): ModifierGroupNameConflict[] {
  const conflicts: ModifierGroupNameConflict[] = [];
  const seenSiblingNames: string[] = [];

  for (const group of groups) {
    const name = group.name.trim();
    if (!name) {
      continue;
    }

    if (catalogLabelExistsIn(name, seenSiblingNames)) {
      conflicts.push({ kind: 'sibling-duplicate', groupTempId: group.tempId, name });
    } else {
      seenSiblingNames.push(name);
    }

    if (!group.definitionId?.trim() && catalogLabelExistsIn(name, catalogNames)) {
      conflicts.push({ kind: 'catalog-duplicate', groupTempId: group.tempId, name });
    }
  }

  return conflicts;
}

export function getModifierGroupNameConflict(
  conflicts: readonly ModifierGroupNameConflict[],
  groupTempId: string
): ModifierGroupNameConflict | null {
  return conflicts.find((conflict) => conflict.groupTempId === groupTempId) ?? null;
}

export function wouldAddModifierGroupNameConflict(
  groups: readonly ProductModifierGroupDraft[],
  catalogNames: readonly string[],
  candidate: { name: string; definitionId?: string | null; tempId?: string }
): ModifierGroupNameConflict | null {
  const hypothetical: ProductModifierGroupDraft = {
    tempId: candidate.tempId ?? '__candidate__',
    id: null,
    definitionId: candidate.definitionId ?? null,
    name: candidate.name,
    description: '',
    selectionType: 'single',
    required: false,
    minSelections: 0,
    maxSelections: 1,
    position: groups.length,
    active: true,
    options: [],
  };

  const conflict = findModifierGroupNameConflicts([...groups, hypothetical], catalogNames).find(
    (item) => item.groupTempId === hypothetical.tempId
  );

  return conflict ?? null;
}

export function hasEmptyInlineModifierGroup(groups: readonly ProductModifierGroupDraft[]): boolean {
  return groups.some((group) => !group.definitionId?.trim() && !group.name.trim());
}

export function buildModifierGroupNameConflictToast(conflict: ModifierGroupNameConflict): {
  toastKey: string;
  toastParams: { name: string };
} {
  if (conflict.kind === 'catalog-duplicate') {
    return {
      toastKey: 'catalog.products.modifierGroupNameDuplicateCatalog',
      toastParams: { name: conflict.name },
    };
  }

  return {
    toastKey: 'catalog.products.modifierGroupNameDuplicate',
    toastParams: { name: conflict.name },
  };
}

export function modifierGroupHasNameConflict(
  conflicts: readonly ModifierGroupNameConflict[],
  groupTempId: string
): boolean {
  return conflicts.some((conflict) => conflict.groupTempId === groupTempId);
}

export function hasModifierGroupNameConflicts(
  groups: readonly ProductModifierGroupDraft[],
  catalogNames: readonly string[]
): boolean {
  return findModifierGroupNameConflicts(groups, catalogNames).length > 0;
}
